// Generic over anything with a name: shopping-catalogue items and foods both use it.
interface Named {
  name: string;
}

/**
 * Match key for names, and the ONLY copy of it in this app — Catalog.tsx used to
 * carry its own, which is how a `.trim()` added here can quietly not apply there.
 *
 * MUST stay byte-identical to nameKey() in backend/src/lib/nutrition.ts and to
 * normalizeForSearch() in mcp-gateway; a divergence shows up as "no match" on a
 * name the server happily links. The shared golden table lives in
 * web/tests/catalog-search.test.ts and backend/tests/nutrition.test.ts.
 *
 * (HighlightedInput.tsx has a same-named helper that is deliberately different:
 * it matches project names by stripping separators, and folds no accents.)
 */
export function normalize(s: string): string {
  return s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function searchCatalog<T extends Named>(items: T[], query: string, limit = 5): T[] {
  const q = normalize(query.trim());
  if (!q) return [];
  return items.filter((it) => normalize(it.name).includes(q)).slice(0, limit);
}

export function sortCatalog<T extends Named>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' })
  );
}
